import * as React from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import { classList } from 'react-classlist-helper'

import {
  GroupHeader,
  GroupHeaderTrigger,
} from '~/client/src/shared/components/GroupHeader'

import {
  ICardListGroup,
  ICardListItem,
  ICardListItemPosition,
  ICardListState,
} from './types'

type VirtualRow<T> =
  | { type: 'header'; group: ICardListGroup<T> }
  | {
      type: 'item'
      item: T
      groupKey?: string
      depth: number
      position: ICardListItemPosition
    }

export interface ICardListHandle {
  /** Scroll row with this id into view. No-op when not rendered. */
  scrollToItemId: (
    id: string,
    opts?: { align?: 'start' | 'center' | 'end' | 'auto' },
  ) => void
  /** Scroll + focus row (or `focusSelector` descendant) after one rAF. */
  focusItemId: (id: string, focusSelector?: string) => void
}

interface ICardListProps<T extends ICardListItem> {
  items: T[]
  listState?: ICardListState<T>
  /** `depth` inherits from the containing group (0 in flat / ungrouped). */
  renderItem: (
    item: T,
    depth?: number,
    position?: ICardListItemPosition,
  ) => React.ReactNode
  renderGroupHeader?: (
    group: ICardListGroup<T>,
    onToggle: () => void,
  ) => React.ReactNode
  header?: React.ReactNode
  loading?: boolean
  empty?: React.ReactNode
  loadingComponent?: React.ReactNode
  className?: string
  role?: string
  ariaLabel?: string
  /** With `role="listbox"`: rows can be selected together. */
  ariaMultiselectable?: boolean
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  /** One-shot initial-mount scroll target. */
  initialScrollToItemId?: string
  /** Default 70. */
  estimatedItemHeight?: number
  /** Default 32. */
  estimatedHeaderHeight?: number
  /** TanStack overscan; default 5. */
  overscan?: number
  /** Drops `size` from `contain` so measured content height bubbles to an auto-sizing parent (`max-h-*` with no `h-*`). */
  parentSizesToContent?: boolean
}

const DEFAULT_ITEM_HEIGHT = 70
const DEFAULT_HEADER_HEIGHT = 32
const DEFAULT_OVERSCAN = 5

function CardListInner<T extends ICardListItem>(
  {
    items,
    listState,
    renderItem,
    renderGroupHeader,
    header,
    loading = false,
    empty,
    loadingComponent,
    className,
    role,
    ariaLabel,
    ariaMultiselectable,
    onKeyDown,
    initialScrollToItemId,
    estimatedItemHeight = DEFAULT_ITEM_HEIGHT,
    estimatedHeaderHeight = DEFAULT_HEADER_HEIGHT,
    overscan = DEFAULT_OVERSCAN,
    parentSizesToContent = false,
  }: ICardListProps<T>,
  ref: React.Ref<ICardListHandle>,
): React.ReactElement {
  const parentRef = useRef<HTMLDivElement>(null)

  // `visibleGroups` is cascade-filtered (excludes bands under a collapsed ancestor);
  // depend on inner arrays — `listState` is a fresh object every render.
  const renderedGroups = useMemo(() => {
    if (!listState) return []
    return listState.visibleGroups ?? listState.groups
  }, [listState?.visibleGroups, listState?.groups])

  const hasGroups = renderedGroups.length > 0

  const virtualRows = useMemo((): VirtualRow<T>[] => {
    if (!hasGroups || !listState) {
      return items.map((item, index) => ({
        type: 'item' as const,
        item,
        groupKey: undefined,
        depth: 0,
        position: { isFirst: index === 0, isLast: index === items.length - 1 },
      }))
    }

    const rows: VirtualRow<T>[] = []
    renderedGroups.forEach(group => {
      rows.push({ type: 'header', group })

      if (!group.isCollapsed) {
        group.items.forEach((item, index) => {
          rows.push({
            type: 'item',
            item,
            groupKey: group.key,
            depth: group.depth,
            position: {
              isFirst: index === 0,
              isLast: index === group.items.length - 1,
            },
          })
        })
      }
    })
    return rows
  }, [hasGroups, items, listState, renderedGroups])

  const virtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: index =>
      virtualRows[index]?.type === 'header'
        ? estimatedHeaderHeight
        : estimatedItemHeight,
    overscan,
    // Stable identity, not positional index — a group toggle can swap header/item
    // at a given position. `?.` guard handles stale indices mid-render.
    getItemKey: (index: number) => {
      const row = virtualRows[index]
      if (!row) return `__stale:${index}`
      if (row.type === 'header') return `header:${row.group.key}`
      return `item:${row.groupKey ?? 'ungrouped'}:${row.item.id}`
    },
  })

  const hasScrolledRef = useRef(false)

  useEffect(() => {
    if (!initialScrollToItemId || hasScrolledRef.current) return
    const index = virtualRows.findIndex(
      row => row.type === 'item' && row.item.id === initialScrollToItemId,
    )
    if (index < 0) return
    virtualizer.scrollToIndex(index, { align: 'start' })
    hasScrolledRef.current = true
  }, [initialScrollToItemId, virtualRows, virtualizer])

  const findItemIndex = useCallback(
    (id: string): number =>
      virtualRows.findIndex(row => row.type === 'item' && row.item.id === id),
    [virtualRows],
  )

  const escapeForAttr = (value: string): string =>
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(value)
      : value.replace(/"/g, '\\"')

  // Cancel pending rAF on rapid keypresses / unmount — otherwise focus races scroll.
  const focusRafRef = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (focusRafRef.current !== null) {
        cancelAnimationFrame(focusRafRef.current)
        focusRafRef.current = null
      }
    },
    [],
  )

  useImperativeHandle(
    ref,
    () => ({
      scrollToItemId(id, opts) {
        const index = findItemIndex(id)
        if (index < 0) return
        virtualizer.scrollToIndex(index, { align: opts?.align ?? 'auto' })
      },
      focusItemId(id, focusSelector) {
        const index = findItemIndex(id)
        if (index < 0) return
        virtualizer.scrollToIndex(index, { align: 'auto' })
        if (focusRafRef.current !== null) {
          cancelAnimationFrame(focusRafRef.current)
        }
        focusRafRef.current = requestAnimationFrame(() => {
          focusRafRef.current = null
          const wrapper = parentRef.current?.querySelector<HTMLElement>(
            `[data-item-id="${escapeForAttr(id)}"]`,
          )
          if (!wrapper) return
          const target = focusSelector
            ? (wrapper.querySelector<HTMLElement>(focusSelector) ?? wrapper)
            : wrapper
          target.focus()
        })
      },
    }),
    [findItemIndex, virtualizer],
  )

  const virtualItems = virtualizer.getVirtualItems()

  if (loading && items.length === 0) {
    return (
      <div
        className={classList({
          'tw:flex tw:flex-col tw:flex-1 tw:min-h-0': true,
          [className || '']: !!className,
        })}
      >
        {header}
        {loadingComponent}
      </div>
    )
  }

  if (!loading && items.length === 0 && empty) {
    return (
      <div
        className={classList({
          'tw:flex tw:flex-col tw:flex-1 tw:min-h-0': true,
          [className || '']: !!className,
        })}
      >
        {header}
        {empty}
      </div>
    )
  }

  const containerClass = classList({
    'tw:flex tw:flex-col tw:flex-1 tw:min-h-0': true,
    [className || '']: !!className,
  })

  return (
    <div className={containerClass}>
      {header}
      <div
        ref={parentRef}
        role={role}
        aria-label={ariaLabel}
        aria-multiselectable={ariaMultiselectable || undefined}
        onKeyDown={onKeyDown}
        style={{
          display: 'block',
          overflow: 'auto',
          // `strict` requires a determined parent height; see `parentSizesToContent` prop.
          contain: parentSizesToContent ? 'layout style paint' : 'strict',
          overflowAnchor: 'none',
          touchAction: 'pan-y',
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualItems.map(virtualItem => {
              // Skip stale index when a group toggle shrinks `virtualRows` mid-render.
              const row = virtualRows[virtualItem.index]
              if (!row) return null
              const itemId = row.type === 'item' ? row.item.id : undefined

              return (
                <div
                  key={String(virtualItem.key)}
                  data-index={virtualItem.index}
                  data-item-id={itemId}
                  ref={virtualizer.measureElement}
                  className={classList({
                    'card-list-item': row.type === 'item',
                    // Not :last-child — the virtualizer renders a window.
                    'tw:[&>*]:border-b-0':
                      virtualItem.index === virtualRows.length - 1,
                  })}
                >
                  {row.type === 'header'
                    ? listState &&
                      (renderGroupHeader ? (
                        renderGroupHeader(row.group, () =>
                          listState.toggleGroup(row.group.key),
                        )
                      ) : (
                        <GroupHeader>
                          <GroupHeaderTrigger
                            expanded={!row.group.isCollapsed}
                            onClick={() => listState.toggleGroup(row.group.key)}
                            ariaLabel={row.group.label}
                            label={row.group.label}
                            count={row.group.count}
                          />
                        </GroupHeader>
                      ))
                    : renderItem(row.item, row.depth, row.position)}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// Cast preserves generics that `forwardRef` strips.
const CardList = forwardRef(CardListInner) as <T extends ICardListItem>(
  props: ICardListProps<T> & { ref?: React.Ref<ICardListHandle> },
) => React.ReactElement

export default CardList
